import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BRAND, NAV, IMAGES } from "@/lib/site";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const onDark = !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
   <header
  data-testid="site-header"
  className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
    scrolled
  ? "bg-white/60 backdrop-blur-2xl backdrop-saturate-200 border-b border-black/5 shadow-[0_10px_40px_rgba(15,23,42,0.08)] py-1"
  : "bg-transparent py-2"
  }`}
>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
  className={`flex items-center justify-between transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
    scrolled ? "h-16" : "h-20"
  }`}
>

          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
  <div
    className={`flex items-center justify-center transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
      scrolled
        ? "w-[54px] h-[54px]"
        : "w-[65px] h-[65px]"
    } group-hover:scale-105`}
  >
    <img
      src={IMAGES.logo}
      alt="Stenzia Displays"
      className={`object-contain transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        scrolled
          ? "w-[54px] h-[54px]"
          : "w-[65px] h-[65px]"
      }`}
    />
  </div>
</Link>

          <nav className="hidden lg:flex items-center gap-2 xl:gap-3">
            {NAV.map((n) => (
              <NavLink
                key={n.path}
                to={n.path}
                end={n.path === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `nav-link-underline px-4 py-3 text-[13px] font-medium tracking-wide transition-colors ${
                    isActive ? "is-active " : ""
                  }${
                    onDark
                      ? isActive ? "text-stenzia-gold" : "text-white/90 hover:text-white"
                      : isActive ? "text-stenzia-green" : "text-charcoal hover:text-stenzia-green"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="header-quote-btn"
              className={`btn-quote-luxe ${onDark ? "is-dark" : "is-light"}`}
            >
              Request Quote
              <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 rounded-lg transition-colors ${onDark ? "text-white hover:bg-white/10" : "text-charcoal hover:bg-black/5"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white/80 backdrop-blur-xl border-t border-black/10 animate-in slide-in-from-top duration-300">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-2">
            {NAV.map((n) => (
              <NavLink
                key={n.path}
                to={n.path}
                end={n.path === "/"}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-base font-medium transition-all ${
                    isActive ? "bg-stenzia-green text-white" : "text-charcoal hover:bg-black/5"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="mobile-quote-btn" className="btn-primary justify-center mt-2">
              Request Quote <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
